import { createArticle, createTag } from "./help-types";
import { ManagementRole } from "./api";

// Create all tags.
export const tags = {
    actionConfigurations: createTag("actionConfigurations", "help.tag.actionConfigurations"),
    administrator: createTag("administrator", "help.tag.administrator"),
    fleetUserRole: createTag("fleetUserRole", "help.tag.fleetUserRole"),
    howTo: createTag("howTo", "help.tag.howTo"),
    maintenanceManager: createTag("maintenanceManager", "help.tag.maintenanceManager"),
    managementUserRole: createTag("managementUserRole", "help.tag.managementUserRole"),
    operator: createTag("operator", "help.tag.operator"),
    reports: createTag("reports", "help.tag.reports"),
    roles: createTag("roles", "help.tag.roles"),
    supervisor: createTag("supervisor", "help.tag.supervisor"),
    standardUser: createTag("standardUser", "help.tag.standardUser"),
    truck: createTag("truck", "help.tag.truck"),
    user: createTag("user", "help.tag.user"),
} as const;

// Create all articles.
export const articles = {
    fleetUserRoles: createArticle({
        filename: "fleet-user-roles.md",
        title: "help.article.fleetUserRoles.title",
        summary: "help.article.fleetUserRoles.summary",
        tags: [
            tags.user,
            tags.roles,
            tags.fleetUserRole,
            tags.operator,
            tags.maintenanceManager,
            tags.supervisor,
        ],
    }),
    managementUserRoles: createArticle({
        filename: "management-user-roles.md",
        title: "help.article.managementUserRoles.title",
        summary: "help.article.managementUserRoles.summary",
        tags: [
            tags.user,
            tags.roles,
            tags.managementUserRole,
            tags.standardUser,
            tags.administrator,
        ],
    }),
    roleOperator: createArticle({
        filename: "roles/operator.md",
        title: "help.article.roleOperator.title",
        summary: "help.article.roleOperator.summary",
        tags: [tags.user, tags.roles, tags.fleetUserRole, tags.operator],
    }),
    roleMaintenanceManager: createArticle({
        filename: "roles/maintenance-manager.md",
        title: "help.article.roleMaintenanceManager.title",
        summary: "help.article.roleMaintenanceManager.summary",
        tags: [tags.user, tags.roles, tags.fleetUserRole, tags.maintenanceManager],
    }),
    roleSupervisor: createArticle({
        filename: "roles/supervisor.md",
        title: "help.article.roleSupervisor.title",
        summary: "help.article.roleSupervisor.summary",
        tags: [tags.user, tags.roles, tags.fleetUserRole, tags.supervisor],
    }),
    roleStandardUser: createArticle({
        filename: "roles/standard-user.md",
        title: "help.article.roleStandardUser.title",
        summary: "help.article.roleStandardUser.summary",
        tags: [tags.user, tags.roles, tags.managementUserRole, tags.standardUser],
    }),
    roleAdministrator: createArticle({
        filename: "roles/administrator.md",
        title: "help.article.roleAdministrator.title",
        summary: "help.article.roleAdministrator.summary",
        tags: [tags.user, tags.roles, tags.managementUserRole, tags.administrator],
    }),
    administratorFunctionalities: createArticle({
        filename: "administrator-functionalities.md",
        title: "help.article.administratorFunctionalities.title",
        summary: "help.article.administratorFunctionalities.summary",
        tags: [tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    adminManageUsers: createArticle({
        filename: "admin/manage-users.md",
        title: "help.article.adminManageUsers.title",
        summary: "help.article.adminManageUsers.summary",
        tags: [tags.user, tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    userDefinition: createArticle({
        filename: "user-definition.md",
        title: "help.article.userDefinition.title",
        summary: "help.article.userDefinition.summary",
        tags: [tags.user, tags.roles, tags.fleetUserRole, tags.managementUserRole],
    }),
    howToAddUser: createArticle({
        filename: "howtos/add-user.md",
        title: "help.article.howToAddUser.title",
        summary: "help.article.howToAddUser.summary",
        tags: [tags.user, tags.roles, tags.fleetUserRole, tags.managementUserRole, tags.howTo],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    howToChangePassword: createArticle({
        filename: "howtos/change-password.md",
        title: "help.article.howToChangePassword.title",
        summary: "help.article.howToChangePassword.summary",
        tags: [tags.user, tags.howTo],
    }),
    howToAccessInternalNfcId: createArticle({
        filename: "howtos/accessing-internal-nfc-ids.md",
        title: "help.article.howToAccessInternalNfcId.title",
        summary: "help.article.howToAccessInternalNfcId.summary",
        tags: [tags.user, tags.fleetUserRole, tags.howTo],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    contact: createArticle({
        filename: "contact.md",
        title: "help.article.contact.title",
        summary: "help.article.contact.summary",
        tags: [],
    }),
    fleetUserGroups: createArticle({
        filename: "fleet-user-groups.md",
        title: "help.article.fleetUserGroups.title",
        summary: "help.article.fleetUserGroups.summary",
        tags: [tags.fleetUserRole],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    adminManageTrucks: createArticle({
        filename: "admin/manage-trucks.md",
        title: "help.article.adminManageTrucks.title",
        summary: "help.article.adminManageTrucks.summary",
        tags: [tags.administrator, tags.truck],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    howToAddTruck: createArticle({
        filename: "howtos/add-truck.md",
        title: "help.article.howToAddTruck.title",
        summary: "help.article.howToAddTruck.summary",
        tags: [tags.howTo, tags.truck],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    howToEditTruck: createArticle({
        filename: "howtos/edit-truck.md",
        title: "help.article.howToEditTruck.title",
        summary: "help.article.howToEditTruck.summary",
        tags: [tags.howTo, tags.truck],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    howToRemoveTruck: createArticle({
        filename: "howtos/remove-truck.md",
        title: "help.article.howToRemoveTruck.title",
        summary: "help.article.howToRemoveTruck.summary",
        tags: [tags.howTo, tags.truck],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    adminSystemWideSettings: createArticle({
        filename: "admin/system-wide-settings.md",
        title: "help.article.adminSystemWideSettings.title",
        summary: "help.article.adminSystemWideSettings.summary",
        tags: [tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    adminConfigureChecklists: createArticle({
        filename: "admin/configure-checklists.md",
        title: "help.article.adminConfigureChecklists.title",
        summary: "help.article.adminConfigureChecklists.summary",
        tags: [tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    adminChecklistLineBreaks: createArticle({
        filename: "admin/checklist-line-breaks.md",
        title: "help.article.adminChecklistLineBreaks.title",
        summary: "help.article.adminChecklistLineBreaks.summary",
        tags: [tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    adminChangeSafetyMessage: createArticle({
        filename: "admin/change-safety-message.md",
        title: "help.article.adminChangeSafetyMessage.title",
        summary: "help.article.adminChangeSafetyMessage.summary",
        tags: [tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    truckFleetReport: createArticle({
        filename: "truck-fleet-report.md",
        title: "help.article.truckFleetReport.title",
        summary: "help.article.truckFleetReport.summary",
        tags: [tags.truck, tags.reports],
    }),
    truckFleetProductivityReport: createArticle({
        filename: "truck-fleet-productivity-report.md",
        title: "help.article.truckFleetProductivityReport.title",
        summary: "help.article.truckFleetProductivityReport.summary",
        tags: [tags.truck, tags.reports],
    }),
    truckFleetDriveTimeReport: createArticle({
        filename: "truck-fleet-drive-time-report.md",
        title: "help.article.truckFleetDriveTimeReport.title",
        summary: "help.article.truckFleetDriveTimeReport.summary",
        tags: [tags.truck, tags.reports],
    }),
    operatorTeamOperators: createArticle({
        filename: "operator-team-operators.md",
        title: "help.article.operatorTeamOperators.title",
        summary: "help.article.operatorTeamOperators.summary",
        tags: [tags.operator, tags.reports],
    }),
    operatorTeamProductivityReport: createArticle({
        filename: "operator-team-productivity-report.md",
        title: "help.article.operatorTeamProductivityReport.title",
        summary: "help.article.operatorTeamProductivityReport.summary",
        tags: [tags.operator, tags.reports],
    }),
    operatorTeamLoginReport: createArticle({
        filename: "operator-team-login-report.md",
        title: "help.article.operatorTeamLoginReport.title",
        summary: "help.article.operatorTeamLoginReport.summary",
        tags: [tags.operator, tags.reports],
    }),
    impactStatisticsReport: createArticle({
        filename: "impact-statistics-report.md",
        title: "help.article.impactStatisticsReport.title",
        summary: "help.article.impactStatisticsReport.summary",
        tags: [tags.truck, tags.reports],
    }),
    maintenanceReport: createArticle({
        filename: "maintenance-report.md",
        title: "help.article.maintenanceReport.title",
        summary: "help.article.maintenanceReport.summary",
        tags: [tags.truck, tags.reports],
    }),
    preOpsChecklistsReport: createArticle({
        filename: "pre-ops-checklists-report.md",
        title: "help.article.preOpsChecklistsReport.title",
        summary: "help.article.preOpsChecklistsReport.summary",
        tags: [tags.reports],
    }),
    safetyMessageReport: createArticle({
        filename: "safety-message-report.md",
        title: "help.article.safetyMessageReport.title",
        summary: "help.article.safetyMessageReport.summary",
        tags: [tags.reports],
    }),
    howToCreateAccessControlGroups: createArticle({
        filename: "howtos/create-access-control-groups.md",
        title: "help.article.howToCreateAccessControlGroups.title",
        summary: "help.article.howToCreateAccessControlGroups.summary",
        tags: [tags.administrator],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    downloadDocuments: createArticle({
        filename: "download-documents.md",
        title: "help.article.downloadDocuments.title",
        summary: "help.article.downloadDocuments.summary",
        tags: [tags.fleetUserRole, tags.operator, tags.maintenanceManager, tags.supervisor],
    }),
    actionConfigsIntroduction: createArticle({
        filename: "action_configs/introduction.md",
        title: "help.article.actionConfigsIntroduction.title",
        summary: "help.article.actionConfigsIntroduction.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsCreating: createArticle({
        filename: "action_configs/creating-configuration.md",
        title: "help.article.actionConfigsCreating.title",
        summary: "help.article.actionConfigsCreating.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsModifying: createArticle({
        filename: "action_configs/modifying-configuration.md",
        title: "help.article.actionConfigsModifying.title",
        summary: "help.article.actionConfigsModifying.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsImportingAndInterconnectingNodes: createArticle({
        filename: "action_configs/importing-and-interconnecting-nodes.md",
        title: "help.article.actionConfigsImportingAndInterconnectingNodes.title",
        summary: "help.article.actionConfigsImportingAndInterconnectingNodes.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsIntermediateNodesDescription: createArticle({
        filename: "action_configs/intermediate-nodes-description.md",
        title: "help.article.actionConfigsIntermediateNodesDescription.title",
        summary: "help.article.actionConfigsIntermediateNodesDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsInputNodesDescription: createArticle({
        filename: "action_configs/input-nodes-description.md",
        title: "help.article.actionConfigsInputNodesDescription.title",
        summary: "help.article.actionConfigsInputNodesDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsOutputNodesDescription: createArticle({
        filename: "action_configs/output-nodes-description.md",
        title: "help.article.actionConfigsOutputNodesDescription.title",
        summary: "help.article.actionConfigsOutputNodesDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsGoDriveZoneFunctionDescriptions: createArticle({
        filename: "action_configs/go-drive-zone-function-descriptions.md",
        title: "help.article.actionConfigsGoDriveZoneFunctionDescriptions.title",
        summary: "help.article.actionConfigsGoDriveZoneFunctionDescriptions.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsExampleTemplateZoneConfiguration: createArticle({
        filename: "action_configs/example-template-for-zone-configuration.md",
        title: "help.article.actionConfigsExampleTemplateZoneConfiguration.title",
        summary: "help.article.actionConfigsExampleTemplateZoneConfiguration.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsSpeedFunctionDescription: createArticle({
        filename: "action_configs/speed-function-description.md",
        title: "help.article.actionConfigsSpeedFunctionDescription.title",
        summary: "help.article.actionConfigsSpeedFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsGuestFunctionDescription: createArticle({
        filename: "action_configs/guest-function-description.md",
        title: "help.article.actionConfigsGuestFunctionDescription.title",
        summary: "help.article.actionConfigsGuestFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsSafeZoneFunctionDescription: createArticle({
        filename: "action_configs/safe-zone-function-description.md",
        title: "help.article.actionConfigsSafeZoneFunctionDescription.title",
        summary: "help.article.actionConfigsSafeZoneFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsAccessFunctionDescription: createArticle({
        filename: "action_configs/access-function-description.md",
        title: "help.article.actionConfigsAccessFunctionDescription.title",
        summary: "help.article.actionConfigsAccessFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsCounterFunctionDescription: createArticle({
        filename: "action_configs/counter-function-description.md",
        title: "help.article.actionConfigsCounterFunctionDescription.title",
        summary: "help.article.actionConfigsCounterFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsCrossWalkFunctionDescription: createArticle({
        filename: "action_configs/crosswalk-function-description.md",
        title: "help.article.actionConfigsCrossWalkFunctionDescription.title",
        summary: "help.article.actionConfigsCrossWalkFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
    actionConfigsCounterPlusFunctionDescription: createArticle({
        filename: "action_configs/counter-plus-function-description.md",
        title: "help.article.actionConfigsCounterPlusFunctionDescription.title",
        summary: "help.article.actionConfigsCounterPlusFunctionDescription.summary",
        tags: [tags.actionConfigurations],
        access: ManagementRole.INSTANCE_ADMIN,
    }),
} as const;
